import {
  dateNoon,
  formatDate,
  formatToCsv,
  getUserFullName,
  isValidDate,
  objectById,
  objectFieldsToCsv,
  priorityLabel,
  searchFieldsHaveValues,
  stripHTML,
  taskNameWithOptionalRepeatCountSuffix
} from 'src/utilities/utils'
import Constants from 'src/utilities/constants'
import Vue from 'vue'
import _, {camelCase} from 'lodash'
import {hydrateTaskSearch} from 'src/utilities/ptracApiHelper'
import {make} from 'vuex-pathify'
import {resetResults} from '@/utilities/vuex2'
import {ptracPatchEntity} from '@/utilities/ptracApi'
import {createAndDownloadWorksheet} from '@/utilities/excel'
import {taskSearch} from '@/utilities/taskSearchHelper'

const initialState = {
  query: {
    name: null,
    search: '',
    jobId: null,
    priority: null,
    status: [],
    assigneeIds: [],
    ownerId: null,
    clientId: null,
    builderId: null,
    communityId: null,
    departmentId: [],
    departmentName: [],
    dueDate: [],
    completionDate: [],
    createdAt: [],
    unassigned: false,
    reinspection: false,
    excludeInvoiceTasks: false,
    poNumber: '',
    poRequired: false,
    noPoNumber: false,
    permitNumber: ''
  },
  returned: {
    jobIds: []
  },
  saveQuery: {
    value: false,
    itemsToRemove: ["dueDate", "completionDate", "createdAt"]
  },
  results: [],
  resultsExcel: [],
  sort: null,
  isBigData: false,
  isPdfsLocked: true,
  loadingProgress: {
    loaded: 0,
    total: 0,
    percent: 0,
    message: ''
  }
}

let state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)
const mutations = make.mutations(state)

getters.results = (state) => {
  return state.results === undefined ? [] : state.results
}

getters.itemsToRemove = (state) => {
  return state.saveQuery.itemsToRemove
}

getters.searchFieldsHaveValues = (state) => {
  const searchFields = [
    'name',
    'priority',
    'status',
    'search',
    'jobId',
    'assigneeIds',
    'ownerId',
    'clientId',
    'builderId',
    'communityId',
    'poNumber',
    'departmentId',
    'permitNumber',
    'unassigned',
    'reinspection',
    'excludeInvoiceTasks',
    'poRequired',
    'noPoNumber',
    'dueDate',
    'completionDate',
    'createdAt'
  ]

  return searchFieldsHaveValues(state.query, searchFields)
}

mutations.results = (state, results) => {
  state.results = results
}

mutations.resultsExcel = (state, results) => {
  state.resultsExcel = results
}

mutations.resetResults = resetResults

mutations.resetQuery = (state) => {
  for (let f in state.query) {
    Vue.set(state.query, f, initialState.query[f])
  }
}

mutations.resetJobIds = (state) => {
  state.returned.jobIds = []
}

export default{
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async fetchSearchResultsAndDownloadToExcel({state, commit, dispatch, rootState, rootGetters}, tableColumns) {
      const esCreds = rootGetters['config/byName'](Constants.CONFIGS.ELASTICSEARCH_UN_PW)

      let hasMoreResults = true
      let from = 0
      const allTasks = []

      while (hasMoreResults) {
        hasMoreResults = false

        const response = await taskSearch(esCreds, state.query, from)

        from += Constants.ELASTICSEARCH.BIG_DATA_RESULTS_THRESHOLD

        if (response.hits.hits.length > 0) {
          state.loadingProgress.total = response.hits.total.value

          const hydratedTasks = await hydrateTaskSearch(
            response.hits.hits,
            rootState.tasks.templates.results,
            rootState.users.results,
            state.loadingProgress
          )
          allTasks.push(...hydratedTasks)

          state.loadingProgress.loaded = allTasks.length

          hasMoreResults = true
        }
      }

      commit('resultsExcel', allTasks)

      await dispatch('resultsToExcel', {
        columns: tableColumns,
        data: allTasks
      })
    },
    async fetchSearchResults({state, commit, rootState, rootGetters}) {
      const esCreds = rootGetters['config/byName'](Constants.CONFIGS.ELASTICSEARCH_UN_PW)
      const response = await taskSearch(esCreds, state.query, 0)

      if (response.hits.hits.length > 0) {
        const total = response.hits.hits.length
        state.loadingProgress.total = total

        if (response.hits.hits.length >= Constants.ELASTICSEARCH.BIG_DATA_RESULTS_THRESHOLD) {
          state.isBigData = true
        }

        const tasks = await hydrateTaskSearch(
          response.hits.hits,
          rootState.tasks.templates.results,
          rootState.users.results,
          state.loadingProgress
        )

        state.loadingProgress.message = 'Rendering table, almost there ...'
        state.loadingProgress.loaded = total
        state.loadingProgress.percent = 100

        commit('results', tasks)
        commit('resetJobIds')
      }

      return response
    },
    async bulkUpdate({}, payload) {
      const promises = []
      for (let task of payload.tasks) {
        let patch = {}

        if (payload.hasOwnProperty('assigneeId') && payload.assigneeId != null) {
          patch.assigneeId = payload.assigneeId
        }

        if (payload.hasOwnProperty('dueDate') && payload.dueDate != null) {
          patch.dueDate = dateNoon(payload.dueDate)
        }

        if (payload.hasOwnProperty('status') && payload.status != null) {
          patch.status = payload.status
        }

        promises.push(ptracPatchEntity('tasks/' + task._id, patch))
      }

      return await Promise.all(promises)
    },
    updateTaskInSearchResults({state, rootState}, task) {
      const index = state.results.findIndex(result => result._id === task.id)
      const result = state.results[index]

      if (result) {
        result._source.status = task.status
        result._source.priority = task.priority
        result._source.priorityLabel = priorityLabel(task.priority)
        result._source.assigneeId = task.assigneeId
        result._source.dueDateFormatted = formatDate(task.dueDate)
        result._source.dueDate = task.dueDate
        result._source.completionDateFormatted = formatDate(task.completionDate)
        if (task.fieldValues.poNumber) {
          result._source.fieldValues.poNumber = task.fieldValues.poNumber
        }

        const owner = objectById(rootState.users.results, task.ownerId)
        result._source.owner = ''
        if (owner) {
          result._source.owner = getUserFullName(owner)
        }

        const assignee = objectById(rootState.users.results, task.assigneeId)
        result._source.assignee = ''
        if (assignee) {
          result._source.assignee = getUserFullName(assignee)
        }

        Vue.set(state.results, index, result)
      }
    },
    resultsToExcel({state}, payload) {
      const fileName = 'task-search-results.xlsx'
      const tabName = 'Tasks'

      const headerRow = []
      const columnNames = []
      for (const column of payload.columns) {
        const key = camelCase(column.label)

        columnNames.push({
          key,
          prop: column.sortProp
        })

        headerRow.push({
          header: column.label,
          key: key,
          width: 30
        })
      }

      const rows = []
      payload.data.forEach((taskResult) => {
        const row = {}
        columnNames.forEach((columnName) => {
          if (columnName.key === 'taskFields') {
            row[columnName.key] = objectFieldsToCsv(_.get(taskResult, columnName.prop))
          } else if (columnName.key === 'address') {
            row[columnName.key] = objectFieldsToCsv(_.get(taskResult, columnName.prop))
          } else if (columnName.key === 'clientTypes') {
            row[columnName.key] = formatToCsv(_.get(taskResult, columnName.prop))
          } else if (columnName.key === 'notes') {
            row[columnName.key] = stripHTML(_.get(taskResult, columnName.prop)) || null
          } else if (columnName.key === 'taskName') {
            row[columnName.key] = taskNameWithOptionalRepeatCountSuffix(_.get(taskResult, '_source'))
          } else if (columnName.key === 'price' || columnName.key === 'travelCharge') {
            row[columnName.key] = Number(_.get(taskResult, columnName.prop)) || null
          } else if (columnName.key.includes('date') || columnName.key.includes('Date')) {
            const maybeDate = _.get(taskResult, columnName.prop)
            if (isValidDate(maybeDate)) {
              row[columnName.key] = new Date(maybeDate)
            } else {
              row[columnName.key] = maybeDate
            }
          } else {
            row[columnName.key] = _.get(taskResult, columnName.prop) || null
          }
        })
        rows.push(row)
      })

      createAndDownloadWorksheet(fileName, tabName, headerRow, rows, payload.user)
    }
  }
}
